import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_PALETTE, COLORS } from "../../../components/dashboard/shared/DashboardShared";
import { formatCount, formatPct, formatYmd, formatYmdShort, minutesToHHmm } from "./format";

// Gráfico secundario: entregas por semana y cuántas de esas quedaron después
// del corte. Ambas series son conteos, así que comparten eje Y sin trampas de
// escala; el porcentaje se muestra en el tooltip.

const WeeklyTooltip = ({ active, payload, cutoffMinutes }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const week = payload[0]?.payload;
  if (!week) return null;

  return (
    <div className="ci-chart-tooltip">
      <p className="ci-chart-tooltip__title">
        {t("Semana del {{fecha}}", { fecha: formatYmd(week.weekStart) })}
      </p>
      <p className="ci-chart-tooltip__row">
        <span className="ci-chart-tooltip__dot" style={{ background: CHART_PALETTE[0] }} />
        {t("Entregas")}: <strong>{formatCount(week.deliveries)}</strong>
      </p>
      <p className="ci-chart-tooltip__row">
        <span className="ci-chart-tooltip__dot" style={{ background: COLORS.danger }} />
        {t("Después de las {{corte}}", { corte: minutesToHHmm(cutoffMinutes) })}:{" "}
        <strong>{formatCount(week.afterCutoffCount)}</strong> ({formatPct(week.afterCutoffPct)})
      </p>
    </div>
  );
};

/**
 * @param {{
 *   weekly: Array<{weekStart:string,deliveries:number,afterCutoffCount:number,afterCutoffPct:number|null}>,
 *   cutoffHour: number,
 *   isDark: boolean,
 *   height?: number
 * }} props
 */
export default function WeeklyTrendChart({ weekly, cutoffHour, isDark, height = 280 }) {
  const { t } = useTranslation();

  const tickColor = isDark ? "#D1D5DB" : "#6B7280";
  const gridColor = isDark ? "#374151" : "#E5E7EB";
  const cutoffMinutes = typeof cutoffHour === "number" ? cutoffHour * 60 : null;

  const data = useMemo(
    () => (weekly || []).map((week) => ({ ...week, label: formatYmdShort(week.weekStart) })),
    [weekly]
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 11 }} stroke={gridColor} />
        <YAxis allowDecimals={false} tick={{ fill: tickColor, fontSize: 11 }} stroke={gridColor} width={44} />
        <Tooltip content={<WeeklyTooltip cutoffMinutes={cutoffMinutes} />} cursor={{ fillOpacity: 0.08 }} />
        <Legend iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        <Bar
          dataKey="deliveries"
          name={t("Entregas")}
          fill={CHART_PALETTE[0]}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        />
        <Bar
          dataKey="afterCutoffCount"
          name={t("Después del corte")}
          fill={COLORS.danger}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
